
<script setup lang="ts">
// This component displays a small video preview in the corner
// It will receive the media stream from the parent
import { onMounted, ref, watch } from 'vue';
import { BaseOverlay, BaseIcon } from '@/components/Base';
import { useDeviceOrientation } from '@/composables/useDeviceOrientation';

const video = ref<HTMLVideoElement | null>(null);

const { isLandscape } = useDeviceOrientation();

const props = defineProps<{
  stream: MediaStream | null;
}>();

const emit = defineEmits<{
  click: []
}>();

onMounted(async () => {
  // ensure stream is applied if it was already set before mount
  await applyVideoStream();
});

// Watch for stream changes and update the video element
watch(() => props.stream, async () => {
  console.log('PiP: Stream changed', props.stream);
  await applyVideoStream();
}, { immediate: true });

async function applyVideoStream() {
  if (!video.value) {
    return;
  }

  try {
    video.value.srcObject = props.stream;
    await video.value.play();
  } catch (e) {
    console.warn('PiP: Failed to apply stream to video element', e);
  }
}

function handleClick() {
  emit('click');
}
</script>

<template>
  <div class="pip-container" @click="handleClick">
    <video ref="video" autoplay muted playsinline class="pip-video"></video>
    <BaseOverlay v-if="isLandscape" class="pip-overlay">
      <BaseIcon name="rotate-phone" :size="32" color="white" />
    </BaseOverlay>
  </div>
</template>

<style scoped>
.pip-container {
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
  width: 120px;
  height: 160px;
  border-radius: var(--border-radius-md);
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  background-color: var(--color-background-video, #60605F);
  cursor: pointer;
}

.pip-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
